import type {
    BacktestResults,
    BacktestDayPnl,
    BacktestSignalRow,
    BacktestPnlPoint,
    BacktestWinLossSummary,
    BacktestBarData,
    BacktestOverviewStats,
    BacktestMonthlyReturn,
    BacktestOptimizeData,
} from './types';
import { DayOfWeek, YearMonth, ZoneId } from '@js-joda/core';
import type { LocalDate } from '@js-joda/core';
import type {
    BacktestJobRecord,
    BacktestResultRow,
    ComboSummaryMetrics,
} from '@thinkalpha/platform-ws-client/contracts/backtest.js';
import type { BacktestJobWithResults } from 'src/queries/backtestSpecs';

const DEFAULT_START_BALANCE = 10000;

export type BacktestOverviewSummaryStats = Omit<BacktestOverviewStats, 'bestTradePct' | 'worstTradePct'>;

const UTC = ZoneId.UTC;

// Weekday bars are always shown in Monday-through-Friday order, regardless of
// which days have trades. Weekend trades (if any) are not bucketed here.
const WEEKDAY_BARS: readonly { day: DayOfWeek; label: string }[] = [
    { day: DayOfWeek.MONDAY, label: 'M' },
    { day: DayOfWeek.TUESDAY, label: 'T' },
    { day: DayOfWeek.WEDNESDAY, label: 'W' },
    { day: DayOfWeek.THURSDAY, label: 'Th' },
    { day: DayOfWeek.FRIDAY, label: 'F' },
];

type DayBucket = { pnl: number; trades: number };

function roundTwoDecimals(n: number): number;
function roundTwoDecimals(n: number | null | undefined): number | null;
function roundTwoDecimals(n: number | null | undefined): number | null {
    return n == null ? null : Math.round(n * 100) / 100;
}

function tradeDate(t: BacktestResultRow): LocalDate {
    return t.date ?? t.entryTimestamp.atZone(UTC).toLocalDate();
}

function tradePnlPct(t: BacktestResultRow): number {
    const cost = t.entryPrice * t.numShares;
    return cost > 0 ? (t.netProfit / cost) * 100 : 0;
}

// TODO: bucket in the user's local timezone instead of UTC and surface the zone
// label in the "Times Of The Day" card so users can interpret the buckets.
function hourBucket(t: BacktestResultRow): string {
    const h = t.entryTimestamp.atZone(UTC).hour();
    return `${h}:00-${h + 1}:00`;
}

function priceBucket(price: number): string {
    if (price < 10) return '$0-10';
    if (price < 50) return '$10-50';
    if (price < 100) return '$50-100';
    if (price < 500) return '$100-500';
    return '$500+';
}

function groupByKey(trades: readonly BacktestResultRow[], keyFn: (t: BacktestResultRow) => string): BacktestBarData[] {
    const groups = new Map<string, DayBucket>();
    for (const t of trades) {
        const key = keyFn(t);
        const existing = groups.get(key) ?? { pnl: 0, trades: 0 };
        existing.pnl += t.netProfit;
        existing.trades++;
        groups.set(key, existing);
    }
    return [...groups.entries()].map(([label, data]) => ({
        label,
        trades: data.trades,
        pnl: roundTwoDecimals(data.pnl),
    }));
}

function maxConsecutive(matchingDays: Iterable<string>, allDays: Iterable<string>): number {
    const matching = new Set(matchingDays);
    const sorted = [...allDays].sort();
    let max = 0;
    let current = 0;
    for (const d of sorted) {
        if (matching.has(d)) {
            current++;
            max = Math.max(max, current);
        } else {
            current = 0;
        }
    }
    return max;
}

function computeEquityCurve(
    trades: readonly BacktestResultRow[],
    startBalance: number,
): { equityCurve: BacktestPnlPoint[]; drawdownSeries: BacktestPnlPoint[] } {
    const equityCurve: BacktestPnlPoint[] = [{ x: 0, y: startBalance }];
    const drawdownSeries: BacktestPnlPoint[] = [{ x: 0, y: 0 }];
    let cumulative = startBalance;
    let peak = startBalance;
    trades.forEach((t, i) => {
        cumulative += t.netProfit;
        if (cumulative > peak) peak = cumulative;
        equityCurve.push({ x: i + 1, y: roundTwoDecimals(cumulative) });
        drawdownSeries.push({ x: i + 1, y: roundTwoDecimals(cumulative - peak) });
    });
    return { equityCurve, drawdownSeries };
}

function computeDailyAggregates(trades: readonly BacktestResultRow[]): Map<string, DayBucket> {
    const map = new Map<string, DayBucket>();
    for (const t of trades) {
        const key = tradeDate(t).toString();
        const bucket = map.get(key) ?? { pnl: 0, trades: 0 };
        bucket.pnl += t.netProfit;
        bucket.trades++;
        map.set(key, bucket);
    }
    return map;
}

/**
 * Subset of overview stats derivable purely from `summaryMetrics` and the configured
 * start balance — i.e., everything the BacktestJobRecord already carries. Trade-pct
 * fields are not included here because they require iterating the per-trade rows
 * that only the full BacktestJob carries.
 */
export function overviewStatsFromSummary(
    summary: ComboSummaryMetrics | null,
    startBalance: number,
): BacktestOverviewSummaryStats {
    const totalPnl = summary?.netPnl ?? null;
    const winners = summary?.winners ?? null;
    const losers = summary?.losers ?? null;
    const totalTrades = summary?.totalTrades ?? null;
    const winRate = winners != null && losers != null && winners + losers > 0 ? winners / (winners + losers) : null;
    const avgPnlPerTrade = totalPnl != null && totalTrades != null && totalTrades > 0 ? totalPnl / totalTrades : null;
    // Fees on summaryMetrics are reported as a positive cost; flip the sign so the
    // overview value is uniformly signed (negative = cost) for downstream rendering.
    const totalFees = summary?.totalFees != null ? -Math.abs(summary.totalFees) : null;

    return {
        startBalance,
        totalPnl: roundTwoDecimals(totalPnl),
        totalReturnPct: totalPnl != null && startBalance > 0 ? roundTwoDecimals((totalPnl / startBalance) * 100) : null,
        winRate: winRate != null ? Math.round(winRate * 10000) / 10000 : null,
        winners,
        losers,
        totalTrades,
        profitFactor: roundTwoDecimals(summary?.profitFactor),
        maxDrawdown: roundTwoDecimals(summary?.maxDrawdown),
        avgPnlPerTrade: roundTwoDecimals(avgPnlPerTrade),
        maxWin: roundTwoDecimals(summary?.bestTrade),
        maxLoss: roundTwoDecimals(summary?.worstTrade),
        totalFees: roundTwoDecimals(totalFees),
        avgHoldSeconds: summary?.avgHoldSeconds != null ? Math.round(summary.avgHoldSeconds) : null,
        avgWin: roundTwoDecimals(summary?.avgWin),
        avgLoss: roundTwoDecimals(summary?.avgLoss),
    };
}

/**
 * Synchronous overview-card values derivable from a `BacktestJobRecord` (the list-query
 * item). Use this to render the 5x2 summary cards immediately, before the full BacktestJob
 * fetch resolves.
 */
export function overviewStatsFromRecord(record: BacktestJobRecord): BacktestOverviewSummaryStats {
    const summary = record.summaryMetrics?.[0] ?? null;
    const startBalance = record.spec.strategySettings.positionCurrencySize?.[0] ?? DEFAULT_START_BALANCE;
    return overviewStatsFromSummary(summary, startBalance);
}

function computeOverview(
    trades: readonly BacktestResultRow[],
    summary: ComboSummaryMetrics | null,
    startBalance: number,
): BacktestOverviewStats {
    const tradePcts = trades.map(tradePnlPct);
    const bestTradePct = tradePcts.reduce((max, v) => Math.max(max, v), -Infinity);
    const worstTradePct = tradePcts.reduce((min, v) => Math.min(min, v), Infinity);

    return {
        ...overviewStatsFromSummary(summary, startBalance),
        bestTradePct: roundTwoDecimals(bestTradePct),
        worstTradePct: roundTwoDecimals(worstTradePct),
    };
}

function computeMonthlyReturns(trades: readonly BacktestResultRow[], startBalance: number): BacktestMonthlyReturn[] {
    const map = new Map<string, { month: YearMonth; pnl: number }>();
    for (const t of trades) {
        const month = YearMonth.from(tradeDate(t));
        const key = month.toString();
        const existing = map.get(key);
        if (existing) {
            existing.pnl += t.netProfit;
        } else {
            map.set(key, { month, pnl: t.netProfit });
        }
    }
    return [...map.values()]
        .sort((a, b) => a.month.compareTo(b.month))
        .map(({ month, pnl }) => ({
            month,
            return: startBalance > 0 ? Math.round((pnl / startBalance) * 1e6) / 1e6 : 0,
        }));
}

function computeWinLossSummaries(dailyMap: Map<string, DayBucket>): {
    winningSummary: BacktestWinLossSummary;
    losingSummary: BacktestWinLossSummary;
} {
    const winningDays: [string, number][] = [];
    const losingDays: [string, number][] = [];
    for (const [date, { pnl }] of dailyMap) {
        if (pnl > 0) winningDays.push([date, pnl]);
        else if (pnl < 0) losingDays.push([date, pnl]);
    }
    const totalDays = dailyMap.size;
    const allDates = [...dailyMap.keys()];

    function summarize(entries: [string, number][]): BacktestWinLossSummary {
        const total = entries.reduce((s, [, p]) => s + p, 0);
        return {
            days: entries.length,
            percent: totalDays > 0 ? Math.round((entries.length / totalDays) * 10000) / 100 : 0,
            avgAmount: entries.length > 0 ? roundTwoDecimals(total / entries.length) : 0,
            totalAmount: roundTwoDecimals(total),
            maxConsecutive: maxConsecutive(
                entries.map(([d]) => d),
                allDates,
            ),
        };
    }

    return { winningSummary: summarize(winningDays), losingSummary: summarize(losingDays) };
}

function computeSignals(trades: readonly BacktestResultRow[]): BacktestSignalRow[] {
    return trades.map((t, i) => ({
        id: String(i),
        index: i + 1,
        symbol: t.symbol,
        side: t.direction > 0 ? 'long' : 'short',
        entryTimestamp: t.entryTimestamp,
        exitTimestamp: t.exitTimestamp,
        entryPrice: t.entryPrice,
        exitPrice: t.exitPrice,
        qty: t.numShares,
        fees: t.fees,
        pnl: t.netProfit,
        pnlPct: tradePnlPct(t),
    }));
}

function computeDaysOfWeek(trades: readonly BacktestResultRow[]): BacktestBarData[] {
    const buckets = new Map<DayOfWeek, DayBucket>(WEEKDAY_BARS.map(({ day }) => [day, { pnl: 0, trades: 0 }]));
    for (const t of trades) {
        const bucket = buckets.get(tradeDate(t).dayOfWeek());
        if (!bucket) continue;
        bucket.pnl += t.netProfit;
        bucket.trades++;
    }
    return WEEKDAY_BARS.map(({ day, label }) => {
        const { pnl, trades: count } = buckets.get(day)!;
        return { label, trades: count, pnl: roundTwoDecimals(pnl) };
    });
}

function computeOptimize(trades: readonly BacktestResultRow[]): BacktestOptimizeData {
    return {
        daysOfWeek: computeDaysOfWeek(trades),
        sector: groupByKey(trades, () => 'Unknown'),
        timesOfDay: groupByKey(trades, hourBucket),
        marketCap: groupByKey(trades, () => 'Unknown'),
        country: groupByKey(trades, () => 'Unknown'),
        price: groupByKey(trades, (t) => priceBucket(t.entryPrice)),
    };
}

function dailyMapToRecord(dailyMap: Map<string, DayBucket>): Record<string, BacktestDayPnl> {
    const result: Record<string, BacktestDayPnl> = {};
    for (const [date, { pnl, trades }] of dailyMap) {
        result[date] = { trades, pnl: roundTwoDecimals(pnl) };
    }
    return result;
}

/**
 * Transform a completed BacktestJob into display-ready BacktestResults.
 */
export function jobToResults(job: BacktestJobWithResults): BacktestResults | null {
    if (!job.results || job.results.length === 0) return null;
    const trades = job.results;
    const summary = job.summaryMetrics?.[0] ?? null;
    const startBalance = job.spec.strategySettings.positionCurrencySize?.[0] ?? DEFAULT_START_BALANCE;

    const dailyMap = computeDailyAggregates(trades);
    const { equityCurve, drawdownSeries } = computeEquityCurve(trades, startBalance);
    const { winningSummary, losingSummary } = computeWinLossSummaries(dailyMap);
    // monthlyReturns is sorted ascending, so the first entry is the first-trade month.
    const monthlyReturns = computeMonthlyReturns(trades, startBalance);
    const initialMonth = job.spec.startDate
        ? YearMonth.from(job.spec.startDate)
        : (monthlyReturns[0]?.month ?? YearMonth.now());

    return {
        overview: computeOverview(trades, summary, startBalance),
        equityCurve,
        drawdownSeries,
        monthlyReturns,
        dailyPnl: dailyMapToRecord(dailyMap),
        winningSummary,
        losingSummary,
        signals: computeSignals(trades),
        optimize: computeOptimize(trades),
        initialMonth,
    };
}
